import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, X, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';

// Training categories
const trainingCategories = [
  'Training',
  'Competition Prep',
  'Drills',
  'Conditioning',
  'Rolling',
  'Open Mat'
];

// Modalities
const modalities = ['Gi', 'No-Gi'];

// Technique categories (reusing from TrainingNotesModal)
const techniqueCategories = [
  {
    name: 'Guard Types',
    techniques: [
      'Closed Guard',
      'Open Guard',
      'Half Guard',
      'Butterfly Guard',
      'Worm Guard',
      'Reverse De La Riva Guard',
      '50/50 Guard',
      'Sitting Guard',
      'No-Gi Specific Guards',
    ],
  },
  {
    name: 'Guard Passing',
    techniques: [
      'Pressure Passing',
      'Torque/Leg Drag Passing',
      'Leg Weave Passing',
      'Floating/Smash Passing',
      'Movement-Based Passing',
      'Folding Passes',
      'No-Gi Specific Passes',
    ],
  },
  {
    name: 'Submissions',
    techniques: [
      'Armlocks',
      'Chokes',
      'Leg Locks',
      'Shoulder Locks',
      'Spine/Neck Cranks',
      'No-Gi Specific Submissions',
    ],
  },
  {
    name: 'Sweeps & Takedowns',
    techniques: [
      'Guard Sweeps',
      'Half Guard Sweeps',
      'Butterfly Sweeps',
      'De La Riva Sweeps',
      'Takedowns from Standing',
      'Counter Takedowns',
      'Pulling Guard',
    ],
  },
  {
    name: 'Escapes & Reversals',
    techniques: [
      'Mount Escapes',
      'Back Escapes',
      'Side Control Escapes',
      'North South Escapes',
      'Submission Escapes',
      'Turtle Escapes',
      'Half Guard Escapes',
    ],
  },
];

export interface TrainingFilters {
  types: string[];
  modalities: string[];
  startDate: Date | null;
  endDate: Date | null;
  instructor: any | null;
  statuses: string[];
  techniques: string[];
  categories: string[];
}

interface TrainingFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: TrainingFilters) => void;
  onClearFilters: () => void;
  currentFilters: TrainingFilters;
  hasActiveFilters: boolean;
}

export const TrainingFilterModal: React.FC<TrainingFilterModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  currentFilters,
  hasActiveFilters
}) => {
  const [filters, setFilters] = useState<TrainingFilters>(currentFilters);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [showTechniques, setShowTechniques] = useState(false);
  const { t } = useI18n();

  // Training types based on the actual database values
  const trainingTypes = [
    t('calendar.group_class'),
    'Private Class'
  ];

  useEffect(() => {
    if (isOpen) {
      setFilters(currentFilters);
    }
  }, [isOpen, currentFilters]);

  const handleFilterChange = (key: keyof TrainingFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTypeToggle = (type: string) => {
    setFilters(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type]
    }));
  };

  const handleModalityToggle = (modality: string) => {
    setFilters(prev => ({
      ...prev,
      modalities: prev.modalities.includes(modality)
        ? prev.modalities.filter(m => m !== modality)
        : [...prev.modalities, modality]
    }));
  };

  const handleStatusToggle = (status: string) => {
    setFilters(prev => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter(s => s !== status)
        : [...prev.statuses, status]
    }));
  };

  const handleTechniqueToggle = (technique: string) => {
    setFilters(prev => ({
      ...prev,
      techniques: prev.techniques.includes(technique)
        ? prev.techniques.filter(t => t !== technique)
        : [...prev.techniques, technique]
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => {
      if (prev.includes(categoryName)) {
        return prev.filter(cat => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const isCategoryOpen = (categoryName: string) => {
    return openCategories.includes(categoryName);
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    const emptyFilters: TrainingFilters = {
      types: [],
      modalities: [],
      startDate: null,
      endDate: null,
      instructor: null,
      statuses: [],
      techniques: [],
      categories: [] // Clear categories as well
    };
    setFilters(emptyFilters);
    onClearFilters();
    onClose();
  };

  const handleClose = () => {
    setFilters(currentFilters); // Reset to current filters
    onClose();
  };

  const statusOptions = [t('calendar.completed'), t('calendar.missed'), t('calendar.scheduled')];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-2xl bg-white rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-black">
            Filters
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Customize your training view by selecting filters
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          {/* Training Type */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Training Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {trainingTypes.map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`type-${type}`}
                    checked={filters.types.includes(type)}
                    onCheckedChange={() => handleTypeToggle(type)}
                  />
                  <label htmlFor={`type-${type}`} className="text-xs text-black">
                    {type}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Modality */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Modality
            </label>
            <div className="grid grid-cols-2 gap-2">
              {modalities.map((modality) => (
                <div key={modality} className="flex items-center space-x-2">
                  <Checkbox
                    id={`modality-${modality}`}
                    checked={filters.modalities.includes(modality)}
                    onCheckedChange={() => handleModalityToggle(modality)}
                  />
                  <label htmlFor={`modality-${modality}`} className="text-xs text-black">
                    {modality}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Period
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                <Input
                  type="date"
                  value={filters.startDate ? format(filters.startDate, "yyyy-MM-dd") : ""}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    handleFilterChange('startDate', date);
                  }}
                  className="text-black border-black bg-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Date</label>
                <Input
                  type="date"
                  value={filters.endDate ? format(filters.endDate, "yyyy-MM-dd") : ""}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    handleFilterChange('endDate', date);
                  }}
                  className="text-black border-black bg-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Training Category */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Training Category
            </label>
            <div className="grid grid-cols-2 gap-2">
              {trainingCategories.map((category) => (
                <div key={category} className="flex items-center space-x-2">
                  <Checkbox
                    id={`category-${category}`}
                    checked={filters.categories.includes(category)}
                    onCheckedChange={() => handleCategoryToggle(category)}
                  />
                  <label htmlFor={`category-${category}`} className="text-xs text-black">
                    {category}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Instructor */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('profile.instructor')}</h3>
            <InstructorAutocomplete
              value={filters.instructor}
              onChange={(instructor) => handleFilterChange('instructor', instructor)}
              placeholder="Type a name or a username"
              className="text-black border-black"
              containerClassName="bg-white"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map((status) => (
                <div key={status} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${status}`}
                    checked={filters.statuses.includes(status)}
                    onCheckedChange={() => handleStatusToggle(status)}
                  />
                  <label htmlFor={`status-${status}`} className="text-xs text-black">
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Techniques */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-black">
                Techniques
              </label>
              <button
                type="button"
                onClick={() => setShowTechniques(!showTechniques)}
                className="text-xs text-[#D72E16] hover:text-[#B8251A] font-medium underline"
              >
                {showTechniques ? 'Hide' : 'Show'} techniques
              </button>
            </div>
            
            {!showTechniques && filters.techniques.length === 0 && (
              <div className="text-center mt-3 mb-3">
                <p className="text-xs font-medium text-gray-600">
                  {t('calendar.techniques_filter_description')}
                </p>
              </div>
            )}

            {filters.techniques.length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-gray-600 mb-2">
                  Selected: {filters.techniques.length} technique{filters.techniques.length !== 1 ? 's' : ''}
                </div>
                <div className="flex flex-wrap gap-1">
                  {filters.techniques.map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 bg-[#D72E16] text-white rounded-full text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {showTechniques && (
              <div className="space-y-3 mt-4">
                {techniqueCategories.map((category) => (
                  <Collapsible 
                    key={category.name} 
                    open={isCategoryOpen(category.name)} 
                    onOpenChange={() => toggleCategory(category.name)}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      <span>{category.name}</span>
                      {isCategoryOpen(category.name) ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="flex flex-wrap gap-1">
                        {category.techniques.map((technique) => {
                          const isSelected = filters.techniques.includes(technique);
                          return (
                            <button
                              key={technique}
                              type="button"
                              onClick={() => handleTechniqueToggle(technique)}
                              className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${
                                isSelected
                                  ? 'bg-[#D72E16] text-white border-[#D72E16]'
                                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                              }`}
                            >
                              {technique}
                            </button>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3">
          <Button
            onClick={handleClear}
            variant="outline"
            className="flex-1 border-black text-black hover:bg-gray-50 rounded-full"
          >
            Clear
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1 bg-[#D72E16] hover:bg-[#B8251A] text-white rounded-full"
          >
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 